import React, { JSX, useEffect, useMemo } from 'react';
import { Box, Center, Grid, useColorModeValue } from '@chakra-ui/react';
import { NavigationBar } from '@hugotomazi/capacitor-navigation-bar';
import { unwrapResult } from '@reduxjs/toolkit';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Outlet, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { BottomNav } from 'clipsal-cortex-ui/src/components/bottom-nav/BottomNav';
import { CenteredLoader } from 'clipsal-cortex-ui/src/components/CenteredLoader';
import { useAppVisibility } from 'clipsal-cortex-utils/src/hooks/use-app-visibility';

import { useReduxDispatch } from '../../app/store';
import { BOTTOM_NAV_HEIGHT, IS_ANDROID, IS_NATIVE, IS_PRODUCTION } from '../../common/constants';
import { useSiteCapabilityCheck } from '../../common/hooks/use-site-capability-check';
import { NOTIFICATION_POLLING_TIME_MS } from '../notifications/constants';
import { useGetNotificationsQuery } from '../notifications/notificationsApi';
import { selectUser } from '../user/userSlice';
import { NAV_LINKS } from './nav-links';
import { useGetSiteConsentByTypeQuery } from './new-user-journey/consentApi';
import { fetchSite, selectSite } from './siteSlice';

// @NOTE: Configure regex for URL path names which will display the bottom nav here.
//        This is configured at the Site-level to avoid re-defining the component in each component it's needed in.
//        View this as a "whitelist" of allowed routes for the bottom nav.
const MATCHING_PATHNAME_REGEX =
  // eslint-disable-next-line max-len
  /\/sites\/\d+\/((home)|(activity)|(devices\/list)|(notifications\/list$)|(ev-charger\/home\/device-status)|(ev-charger\/home\/device-charging-activity)(?!.*\?.*))/;

export function Site() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const user = useSelector(selectUser);
  const { site_id: siteId } = useSelector(selectSite);
  const dispatch = useReduxDispatch();
  const { id } = useParams<{ id: string }>();
  const backgroundColor = useColorModeValue('#ffffff', '#111111');
  const { isError: isSiteConsentsError } = useGetSiteConsentByTypeQuery('SHARE_WITH_INSTALLER');
  const { t } = useTranslation();
  const shouldShowNav = useMemo(
    () => MATCHING_PATHNAME_REGEX.test(pathname) && !search.get('source'),
    [pathname, search]
  );
  const navLinks = useNavLinks(shouldShowNav);

  // @TODO: move colours to theme!
  const bottomNavProps = useColorModeValue(
    {
      itemColor: 'customGrey.500',
      selectedItemColor: 'primaryBranding.600',
      backgroundColor: '#F7F7F7',
      selectedBackgroundColor: '#FFFFFF',
    },
    {
      itemColor: '#CBCBCB',
      selectedItemColor: 'primaryBranding.200',
      backgroundColor: '#293133',
      selectedBackgroundColor: '#363E40',
    }
  );

  useEffect(() => {
    async function setStyle() {
      if (IS_NATIVE && IS_ANDROID) {
        await NavigationBar.setColor({ color: shouldShowNav ? bottomNavProps.backgroundColor : backgroundColor });
      }
    }

    setStyle();
  }, [backgroundColor, bottomNavProps.backgroundColor, shouldShowNav]);

  useEffect(() => {
    async function dispatchFetchSite() {
      try {
        unwrapResult(await dispatch(fetchSite({ siteID: Number(id), isProduction: IS_PRODUCTION })));
      } catch (e) {
        console.error('Failed to fetch site:', e);
        // This could fail if the selected site doesn't exist, or the user doesn't have access to it anymore.
        navigate(`/sites/list`);
      }
    }

    // No site in the store - hydrate from API, select site with ID from params
    if (!siteId) {
      dispatchFetchSite();
    }
  }, [dispatch, id, navigate, siteId, user.isLoggedIn]);

  useEffect(() => {
    // redirect to consent error page if there are any api errors
    if (isSiteConsentsError) navigate(`/consent-error?siteId=${siteId}`);
  }, [isSiteConsentsError, navigate, siteId]);

  return !siteId ? (
    <CenteredLoader text={`${t('Common.loading')}...`} />
  ) : (
    <Grid templateColumns={'1fr'}>
      {shouldShowNav && (
        <BottomNav
          links={navLinks}
          navHeight={BOTTOM_NAV_HEIGHT}
          selectedChildren={<Box position={'absolute'} top={0} height={'3px'} w={'100%'} bg={'primaryBranding.600'} />}
          boxShadow="0px -4px 4px 0px rgba(0, 0, 0, 0.25)"
          {...bottomNavProps}
        />
      )}
      <Outlet />
    </Grid>
  );
}

/**
 * Builds the links to display on the nav.
 *
 * @param shouldShowNav - Whether the bottom nav should be displayed.
 */
function useNavLinks(shouldShowNav: boolean) {
  const { t } = useTranslation();
  const { site_id: siteId } = useSelector(selectSite);
  const { pathname } = useLocation();
  const isAppVisible = useAppVisibility();
  // We poll notifications only when the bottom nav & app is visible.
  const { data: notifications } = useGetNotificationsQuery(
    { limit: 0, offset: 0, read: false, archived: false, siteId },
    { pollingInterval: isAppVisible && shouldShowNav ? NOTIFICATION_POLLING_TIME_MS : 0, skip: !siteId }
  );

  const { siteHasInverter, siteHasMeter, siteHasOnlyEvCharger } = useSiteCapabilityCheck();

  return useMemo(() => {
    let links;
    if (siteHasOnlyEvCharger) {
      links = NAV_LINKS.filter(
        ({ route }) =>
          route.includes('/ev-charger/home/device-status') ||
          route.includes('/ev-charger/home/device-charging-activity') ||
          route === '/notifications'
      );
    } else {
      links = NAV_LINKS.filter(
        ({ route }) => !route.includes('/ev-charger') && (route !== '/notifications' || siteHasMeter || siteHasInverter)
      );
    }

    const currentNavIndex = links.findIndex(({ route }) => pathname.includes(route));

    return links.map(({ route, ariaLabel, icon, activeIcon, ...other }, i) => {
      const direction = currentNavIndex > i ? 'back' : 'forward';
      const hasNotification = route.includes('/notifications') && notifications && notifications.count > 0;
      return {
        ...other,
        route: `/sites/${siteId}${route}?direction=${direction}`,
        ariaLabel: t(`Nav Bar.${ariaLabel}`),
        icon: hasNotification ? NotificationIcon(notifications.count, icon) : icon,
        activeIcon: hasNotification ? NotificationIcon(notifications.count, activeIcon) : activeIcon,
      };
    });
  }, [siteHasMeter, siteHasInverter, siteHasOnlyEvCharger, pathname, notifications, siteId, t]);
}

function NotificationIcon(count: number, icon: JSX.Element) {
  return (
    <Box position={'relative'}>
      {React.cloneElement(icon, { w: '30px', h: '30px' })}
      <Center
        data-testid="num-unread-notifications"
        fontSize={'9px'}
        borderRadius={'50%'}
        pos={'absolute'}
        top={'-3px'}
        right={'-4px'}
        bg={'#BA3430'}
        color={'white'}
        w={'16px'}
        h={'16px'}
      >
        {count >= 100 ? '99+' : count}
      </Center>
    </Box>
  );
}
